import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ShiftResponse } from '@troia-v3/types';
import { MemberAvatarList, StatusPill, Switch } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAddShiftMember, useRemoveShiftMember, useShiftMembers } from '../hooks';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de turno (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + ícone slate + colunas de domínio + menu ••• (última col).
 * Preserva gestão inline de membros (add/remove).
 */
export interface ShiftListItemProps {
  /** Shift data from API */
  shift: ShiftResponse;
  /** Selecionado (seleção em massa) */
  selected?: boolean;
  /** Toggle de seleção */
  onToggleSelect?: (id: string) => void;
  /** Callback when edit is clicked */
  onEdit?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Atalho "Operadores" — navega pro form do turno onde se gerencia membros */
  onManageMembers?: (id: string) => void;
  /** Duplica o turno (clona config; membros não são copiados) */
  onDuplicate?: (id: string) => void;
}

const WEEKDAY_ORDER: { value: number; label: string }[] = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

/** Formata weekdays[] em rótulo compacto (ex.: "Seg–Sex", "Todos os dias"). */
const formatWeekdays = (weekdays?: number[]): string => {
  if (!weekdays || weekdays.length === 0) return '';
  const set = new Set(weekdays);
  if (set.size >= 7) return 'Todos os dias';
  const selected = WEEKDAY_ORDER.filter((d) => set.has(d.value));
  if (selected.length === 0) return '';
  const positions = WEEKDAY_ORDER.map((d, i) => (set.has(d.value) ? i : -1)).filter((i) => i >= 0);
  const first = positions[0] ?? 0;
  const last = positions[positions.length - 1] ?? 0;
  const contiguous = positions.length > 1 && positions.length === last - first + 1;
  if (contiguous) {
    const a = selected[0]?.label ?? '';
    const b = selected[selected.length - 1]?.label ?? '';
    return `${a}–${b}`;
  }
  return selected.map((d) => d.label).join(', ');
};

export const ShiftListItem: React.FC<ShiftListItemProps> = ({
  shift,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageMembers,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  // Fetch shift members
  const { data: members = [] } = useShiftMembers(shift.id);

  // Local state for instant updates
  const [localMembers, setLocalMembers] = React.useState(members);

  // Update local members when prop changes
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Fetch available users for dropdown
  const { data: allUsers = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });

  // Mutations for adding/removing users
  const addMemberMutation = useAddShiftMember();
  const removeMemberMutation = useRemoveShiftMember();

  // Map members to expected format
  const mappedMembers = localMembers.map((m) => ({
    id: m.id,
    name: m.name,
    avatar: m.avatar,
  }));

  const handleAddMember = (userId: string) => {
    // Optimistic update: add to local state immediately
    const newMember = allUsers.find((u) => u.id === userId);
    if (newMember) {
      setLocalMembers([
        ...localMembers,
        {
          id: newMember.id,
          name: newMember.name,
          email: '',
          avatar: newMember.avatar,
          shiftRole: 'primary' as const,
        },
      ]);
    }

    addMemberMutation.mutate({
      shiftId: shift.id,
      data: { userId, role: 'primary' },
    });
  };

  const handleRemoveMember = (userId: string) => {
    // Optimistic update: remove from local state immediately
    setLocalMembers(localMembers.filter((m) => m.id !== userId));

    removeMemberMutation.mutate({
      shiftId: shift.id,
      userId,
    });
  };

  // Format schedule (supports both flat and legacy nested shape)
  const sched = (shift.schedule as { fixedSchedule?: ShiftResponse['schedule'] })?.fixedSchedule ?? shift.schedule;
  const weekdaysLabel = formatWeekdays(sched?.weekdays);
  const timeLabel = sched?.startTime && sched?.endTime ? `${sched.startTime} – ${sched.endTime}` : null;
  const scheduleText = [weekdaysLabel, timeLabel].filter(Boolean).join(' · ') || '—';

  const isActive = shift.status === 'active';

  // Build actions for CardActionsMenu
  const actions: CardAction[] = [];
  if (onEdit) {
    actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(shift.id) });
  } else {
    actions.push({ label: 'Editar', icon: 'edit', onClick: () => navigate(`/settings/shifts/${shift.id}/edit`) });
  }
  if (onManageMembers) actions.push({ label: 'Operadores', icon: 'users', onClick: () => onManageMembers(shift.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(shift.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(shift.id, shift.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(shift.id), variant: 'danger', dividerBefore: true });
  }

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      data-testid={`shift-row-${shift.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(shift.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`shift-check-${shift.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Nome: ícone slate + nome */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#64748b', color: '#ffffff' }}
          >
            <Icon name="clock" className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {shift.name}
            </div>
          </div>
        </div>
      </td>

      {/* Descrição */}
      <td className="px-4 py-3">
        <div className="text-sm max-w-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
          {shift.description || 'Sem descrição'}
        </div>
      </td>

      {/* Tipo */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-medium"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          Fixo
        </span>
      </td>

      {/* Horário */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }}>
        {scheduleText}
      </td>

      {/* Membros */}
      <td className="px-4 py-3">
        <MemberAvatarList
          members={mappedMembers}
          availableUsers={allUsers}
          limit={3}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          canRemoveMembers={false}
          loading={loadingUsers}
          placeholder="Buscar membro..."
          emptyMessage="Sem membros"
        />
      </td>

      {/* Status — F47: switch inline (Switch shared sm); pill só quando não togglável (F62) */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`shift-toggle-${shift.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(shift.id, shift.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            data-testid={`shift-status-${shift.id}`}
          />
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`shift-row-actions-${shift.id}`} />
      </td>
    </tr>
  );
};

ShiftListItem.displayName = 'ShiftListItem';
